import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import {
  getGetMeQueryKey,
  getGetStoreQueryKey,
  useActivateStore,
  useCancelStoreDelete,
  useDeactivateStore,
  useScheduleStoreDelete,
} from "@/api";
import type { Store } from "@/api/schemas/store";
import { getApiErrorMessage } from "@/lib/api/errors";
import { formatPersianDateLong } from "@/lib/format/persian";
import {
  scheduleDeleteSchema,
  type ScheduleDeleteFormValues,
} from "@/lib/validation/settings.schemas";

export type DangerConfirm = "deactivate" | "delete" | null;

/** Danger zone: deactivate / reactivate the store, schedule or cancel its deletion. */
export function useSettingsDanger(storeId: number | null, store: Store | undefined) {
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<DangerConfirm>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    if (storeId === null) return;
    void queryClient.invalidateQueries({ queryKey: getGetStoreQueryKey(storeId) });
    void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
  }, [queryClient, storeId]);

  const onMutationError = (mutationError: unknown) => {
    setConfirm(null);
    setError(getApiErrorMessage(mutationError) ?? "عملیات انجام نشد؛ دوباره تلاش کنید");
  };
  const onMutationSuccess = () => {
    setConfirm(null);
    refresh();
  };

  const { mutate: deactivate, isPending: isDeactivating } = useDeactivateStore({
    mutation: { onSuccess: onMutationSuccess, onError: onMutationError },
  });
  const { mutate: activate, isPending: isActivating } = useActivateStore({
    mutation: { onSuccess: onMutationSuccess, onError: onMutationError },
  });
  const { mutate: cancelDelete, isPending: isCancellingDelete } = useCancelStoreDelete({
    mutation: { onSuccess: onMutationSuccess, onError: onMutationError },
  });

  const form = useForm<ScheduleDeleteFormValues>({
    resolver: zodResolver(scheduleDeleteSchema),
    defaultValues: { confirmName: "" },
  });
  const confirmName = useWatch({ control: form.control, name: "confirmName", defaultValue: "" });

  const { mutate: scheduleDelete, isPending: isSchedulingDelete } = useScheduleStoreDelete({
    mutation: {
      onSuccess: () => {
        form.reset();
        onMutationSuccess();
      },
      onError: onMutationError,
    },
  });

  const shopName = store?.name ?? "";
  const deleteMatches = Boolean(shopName) && confirmName.trim() === shopName.trim();
  const isShopActive = store?.is_active ?? true;

  const onToggleShopActive = useCallback(() => {
    if (storeId === null) return;
    setError(null);
    if (isShopActive) setConfirm("deactivate");
    else activate({ id: storeId });
  }, [activate, isShopActive, storeId]);

  // The form only opens the confirmation; the request goes out from the dialog.
  const onSubmit = form.handleSubmit(() => {
    if (!deleteMatches) return;
    setError(null);
    setConfirm("delete");
  });

  const onConfirm = useCallback(() => {
    if (storeId === null) return;
    if (confirm === "deactivate") deactivate({ id: storeId });
    if (confirm === "delete")
      scheduleDelete({ id: storeId, data: { confirmName: form.getValues("confirmName") } });
  }, [confirm, deactivate, form, scheduleDelete, storeId]);

  const onCancelDelete = useCallback(() => {
    if (storeId === null) return;
    setError(null);
    cancelDelete({ id: storeId });
  }, [cancelDelete, storeId]);

  return {
    form,
    onSubmit,
    shopName,
    isShopActive,
    deleteRequested: Boolean(store?.scheduled_delete_at),
    scheduledDeleteLabel: store?.scheduled_delete_at
      ? formatPersianDateLong(new Date(store.scheduled_delete_at))
      : "",
    deleteMatches,
    isToggling: isDeactivating || isActivating,
    isSchedulingDelete,
    isCancellingDelete,
    onToggleShopActive,
    onCancelDelete,
    error,
    confirm,
    isConfirmPending: isDeactivating || isSchedulingDelete,
    onConfirm,
    onCloseConfirm: () => setConfirm(null),
  };
}

export type SettingsDangerState = ReturnType<typeof useSettingsDanger>;
